import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, useTheme, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, alpha
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from 'recharts';
import { useAuth } from '../../hooks/auth/useAuth';
import { analyticsApi } from '../../services/api';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

const DEFECT_COLORS = {
  percentage: ['#7c4dff', '#9c27b0', '#ce93d8', '#e1bee7'],
  binary: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'],
  degree: ['#ff9800']
};

const getDefectColor = (code, index) => {
  if (['hm_corrosion', 'hm_paint_peeling', 'hm_vegetation', 'hm_moss'].includes(code)) {
    return DEFECT_COLORS.percentage[index % 4];
  }
  if (code === 'hm_slanting') return DEFECT_COLORS.degree[0];
  return DEFECT_COLORS.binary[index % 5];
};

const HighMastAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uninspectedData, setUninspectedData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const [statsRes, uninspectedRes] = await Promise.all([
        analyticsApi.getHighMastDashboardStats(),
        analyticsApi.getHighMastDetails('uninspected', { limit: 50, offset: 0 })
      ]);
      setStats(statsRes.data.data);
      setUninspectedData(uninspectedRes.data.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewOnMap = (asset) => {
    if (asset.center_lat && asset.center_lng) {
      navigate('/map-view', {
        state: {
          selectedAsset: { id: asset.id, name: asset.name, code: asset.code, center_lat: asset.center_lat, center_lng: asset.center_lng },
          flyTo: { lat: asset.center_lat, lng: asset.center_lng, zoom: 18 }
        }
      });
    }
  };

  if (loading) {
    return (
      <DisciplineAccessControl requiredDiscipline="Vertical Asset">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#7c4dff' }} />
        </Box>
      </DisciplineAccessControl>
    );
  }

  if (error) {
    return (
      <DisciplineAccessControl requiredDiscipline="Vertical Asset">
        <Alert severity="error">{error}</Alert>
      </DisciplineAccessControl>
    );
  }

  if (!stats) return null;

  // Prepare chart data
  const defectChartData = stats.defectsByType.map((d, i) => ({
    name: d.defect_type_name,
    count: d.count,
    fill: getDefectColor(d.defect_type_code, i)
  }));

  const severityChartData = stats.defectsBySeverity.map(d => ({
    name: d.severity_name,
    count: d.count,
    fill: d.color_code || '#9e9e9e'
  }));

  return (
    <DisciplineAccessControl requiredDiscipline="Vertical Asset">
      <Box sx={{ maxWidth: '100%' }}>

        {/* Charts Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>

          {/* Defect Distribution by Type */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Defect Distribution by Type
            </Typography>
            {defectChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={defectChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.15)} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                    {defectChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No defects recorded yet</Typography>
              </Box>
            )}
          </Paper>

          {/* Severity Breakdown */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Defect Severity Breakdown
            </Typography>
            {severityChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={severityChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={3} label={({ name, count }) => `${name} (${count})`}>
                    {severityChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No severity data available</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Tables Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

          {/* Top Assets by Defect Count */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Top Assets by Defect Count
            </Typography>
            {stats.topAssetsByDefects.length > 0 ? (
              <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Defects</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topAssetsByDefects.map((asset, index) => (
                      <TableRow key={asset.asset_id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                        <TableCell>
                          <Chip label={index + 1} size="small" sx={{ fontWeight: 700, minWidth: 28, bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.asset_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.section_name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={asset.defect_count} size="small" sx={{ fontWeight: 600, bgcolor: alpha('#f44336', 0.1), color: '#f44336' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">No defects recorded yet</Typography>
              </Box>
            )}
          </Paper>

          {/* Uninspected Assets */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Uninspected Assets
            </Typography>
            {uninspectedData && uninspectedData.length > 0 ? (
              <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.04) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Height</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uninspectedData.map((asset) => (
                      <TableRow key={asset.id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                        <TableCell>
                          <Chip label={asset.code} size="small" sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.dark }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.section || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{asset.height ? `${asset.height}m` : '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View on Map">
                            <IconButton size="small" onClick={() => handleViewOnMap(asset)}
                              sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                              <MapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography color="text.secondary">All assets have been inspected</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </DisciplineAccessControl>
  );
};

export default HighMastAnalytics;
